import { Flex, Input, Text, useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { AuthenticatedComponent } from '../auth/keycloak';
import { usePageDataCache } from '../hooks/usePageDataCache';
import { BrowserView } from 'react-device-detect';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { getSubscribers, getUserSubscription, SubscriptionDef } from '../service/inventory';
import { SubscriptionListItem } from '../component/profileComponents/SubscriptionListItem';
import { getPublicProfiles, UserProfile } from '../service/social';
import { RadioFilter } from '../component/RadioFilter';
import { HiSearch } from 'react-icons/hi';

export const ProfileSubscriptions: AuthenticatedComponent = ({ keycloak }) => {
    const SUBSCRIBERS = 'SUBSCRIBERS';
    const SUBSCRIPTIONS = 'SUBSCRIPTIONS';
    const MUTUALS = 'MUTUALS';

    const toast = useToast();

    const [filter, setFilter] = useState<string>(SUBSCRIBERS);

    const [subscribersLoaded, setSubscribersLoaded] = useState<boolean>(false);
    const [subscriptionsLoaded, setSubscriptionsLoaded] = useState<boolean>(false);

    const [subscribers, setSubscribers] = usePageDataCache<SubscriptionDef[]>(
        'PrivateFollowStats_subscribers',
        [],
    );
    const [subscriptions, setSubscriptions] = usePageDataCache<SubscriptionDef[]>(
        `PrivateFollowStats_subscriptions`,
        [],
    );
    const [mutuals, setMutuals] = usePageDataCache<SubscriptionDef[]>(
        `PrivateFollowStats_mutuals`,
        [],
    );

    const [subscriptionProfiles, setSubscriptionProfiles] = usePageDataCache<{
        [key: string]: UserProfile;
    }>(`PrivateFollowStats_subscription_profiles`, {});

    const [subscriberProfiles, setSubscriberProfiles] = usePageDataCache<{
        [key: string]: UserProfile;
    }>(`PrivateFollowStats_subscriber_profiles`, {});

    const [filteredSubscribers, setFilteredSubscribers] = useState<SubscriptionDef[]>([]);
    const [filteredSubscriptions, setFilteredSubscriptions] = useState<SubscriptionDef[]>([]);
    const [filteredMutuals, setFilteredMutuals] = useState<SubscriptionDef[]>([]);

    useEffect(() => {
        fetchSubscriptions();
        fetchSubscribers();
    }, []);

    useEffect(() => {
        if (subscribers.length === 0 || subscriptions.length === 0) return;

        const matches = subscribers.filter((s1) => {
            return subscriptions.find((s2) => s1.subscription_key.subscribe_target === s2.user_id);
        });
        setMutuals(matches);
        setFilteredMutuals(matches);
    }, [subscribers, subscriptions]);

    const fetchSubscriptions = async () => {
        try {
            const subList = await getUserSubscription(keycloak.subject, '', '', keycloak.token);

            let idList: string[] = [];
            let filteredSubs: SubscriptionDef[] = [];
            subList.map((sub) => {
                if (
                    sub.subscription_key.subscribe_target !== 'streamsix' &&
                    Boolean(sub.status.inactive) !== true
                ) {
                    filteredSubs.push(sub);
                    idList.push(sub.subscription_key.subscribe_target);
                }
            });

            const profileList = await getPublicProfiles(idList);
            setSubscriptionProfiles(profileList);

            setSubscriptions(filteredSubs);
            setFilteredSubscriptions(filteredSubs);
        } catch (e) {
            toast({
                title: 'Something Went Wrong',
                description: (e as Error).message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } finally {
            setSubscriptionsLoaded(true);
        }
    };

    const fetchSubscribers = async () => {
        try {
            const subs = await getSubscribers(keycloak.subject, 20, keycloak.token);

            const idList = subs.data.map((subDef) => {
                return subDef.subscription_key.user_id;
            });
            const profileList = await getPublicProfiles(idList);
            setSubscriberProfiles(profileList);

            setSubscribers(subs.data);
            setFilteredSubscribers(subs.data);
        } catch (e) {
            toast({
                title: 'Something Went Wrong',
                description: (e as Error).message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } finally {
            setSubscribersLoaded(true);
        }
    };

    const updateSearch = (event: any) => {
        setFilteredSubscribers(
            subscribers.filter((s) =>
                subscriberProfiles[s.subscription_key.user_id].username
                    .toLowerCase()
                    .includes(event.target.value.toLowerCase()),
            ),
        );
        setFilteredSubscriptions(
            subscriptions.filter((s) =>
                subscriptionProfiles[s.subscription_key.subscribe_target].username
                    .toLowerCase()
                    .includes(event.target.value.toLowerCase()),
            ),
        );
        setFilteredMutuals(
            mutuals.filter((s) =>
                subscriberProfiles[s.subscription_key.subscribe_target].username
                    .toLowerCase()
                    .includes(event.target.value.toLowerCase()),
            ),
        );
    };

    const getTitle = (): string => {
        switch (filter) {
            case SUBSCRIBERS:
                return `SUBSCRIBERS ${subscribers.length}`;
            case SUBSCRIPTIONS:
                return `SUBSCRIPTIONS ${subscriptions.length}`;
            case MUTUALS:
                return `MUTUALS ${mutuals.length}`;
        }
        return '';
    };

    const getFilteredList = (): SubscriptionDef[] => {
        switch (filter) {
            case SUBSCRIBERS:
                return filteredSubscribers;
            case SUBSCRIPTIONS:
                return filteredSubscriptions;
            case MUTUALS:
                return filteredMutuals;
        }
        return [];
    };

    const renderFilters = () => (
        <RadioFilter
            groupName="gamelist"
            options={[
                {
                    option: SUBSCRIBERS,
                    className: 'profile-follow-filter',
                    selectedClassName: 'profile-follow-filter-selected',
                },
                {
                    option: SUBSCRIPTIONS,
                    className: 'profile-follow-filter',
                    selectedClassName: 'profile-follow-filter-selected',
                },
                {
                    option: MUTUALS,
                    className: 'profile-follow-filter',
                    selectedClassName: 'profile-follow-filter-selected',
                },
            ]}
            defaultOption={SUBSCRIBERS}
            value={filter}
            onRadioChange={(newValue) => setFilter(newValue)}
        />
    );

    const renderListItem = (key: string, sub: SubscriptionDef) => {
        return (
            <SubscriptionListItem
                key={key}
                subcription={sub}
                profile={
                    filter === SUBSCRIBERS
                        ? subscriberProfiles[sub.subscription_key.user_id]
                        : subscriptionProfiles[sub.subscription_key.subscribe_target]
                }
            />
        );
    };

    return (
        <>
            <BrowserView>
                <Flex className="profile-followers-content">
                    <Flex className={'profile-subscriptions'}>
                        <Flex className="following-filters-browser">
                            {renderFilters()}
                            <Flex className="following-search">
                                <Input variant={'unstyled'} onChange={updateSearch} />
                                <Flex className={'following-search-icon'}>
                                    <HiSearch size={'10px'} />
                                </Flex>
                            </Flex>
                        </Flex>
                        <Flex className={'profile-following-list'}>
                            {getFilteredList().length === 0 ? (
                                <Text>No results</Text>
                            ) : (
                                getFilteredList().map((userSubscription, idx) =>
                                    renderListItem(idx.toString(), userSubscription),
                                )
                            )}
                        </Flex>
                    </Flex>
                </Flex>
            </BrowserView>
            <MobileViewPage
                title={getTitle()}
                titleColor="#000"
                titleBackgroundColor="#FECA3C"
                isLoading={!subscribersLoaded || !subscriptionsLoaded}
            >
                <Flex className={'profile-followers-content'}>
                    <Flex className={'divider'} />
                    <Flex className="following-filters">
                        {renderFilters()}
                        <Flex className="following-search">
                            <Input variant={'unstyled'} onChange={updateSearch} />
                            <Flex className={'following-search-icon'}>
                                <HiSearch size={'10px'} />
                            </Flex>
                        </Flex>
                    </Flex>
                    <Flex className={'profile-following-list'}>
                        {getFilteredList().length === 0 ? (
                            <Text>No results</Text>
                        ) : (
                            getFilteredList().map((userSubscription, idx) =>
                                renderListItem(idx.toString(), userSubscription),
                            )
                        )}
                    </Flex>
                </Flex>
            </MobileViewPage>
        </>
    );
};
